<script lang="ts">
    import type * as t from "./types";

    import { fly } from "svelte/transition";
    import { goToNextStep } from "./quiz-controller.svelte";
    import ResponsiveImage from "./ResponsiveImage.svelte";

    export let visible;
    export let waitUntilCanProceed: number;
    export let block: t.Block["settings"];

    const order: "text_media" | "media_text" = block.order;
    const video: t.Shopify.Video | undefined = block.video;
    const imageSrc: string | undefined = block.image;
    let videoSrc: t.Shopify.VideoSource | undefined;
    if (video != null) {
        videoSrc = video.sources?.find((source) => {
            return source.mime_type.startsWith("video");
        });
    }
    const hasMedia = imageSrc != null || videoSrc != null;

    let wait = waitUntilCanProceed;
    let container;
    let canProceed = false;
    let nextButtonOverlayContainer: HTMLDivElement;
    let videoPlayed = false;

    function restartButtonTransition() {
        nextButtonOverlayContainer.style.marginLeft = "0";
        nextButtonOverlayContainer.style.transition = "none";
        startButtonTransition();
    }

    function startButtonTransition() {
        window.requestAnimationFrame(() => {
            nextButtonOverlayContainer.style.transition = `margin-left ${wait}s linear`;
            nextButtonOverlayContainer.style.marginLeft = "100%";
            nextButtonOverlayContainer.classList.add("shadow-xl");
        });
    }

    function setupVideo() {
        wait = Number.MAX_SAFE_INTEGER;
        setTimeout(() => {
            if (videoPlayed) {
                return;
            }
            wait = waitUntilCanProceed;
            restartButtonTransition();
            setTimeout(() => {
                canProceed = true;
            }, wait * 1000);
        }, 2000);
    }

    function handlePlay() {
        if (videoPlayed) {
            return;
        }

        videoPlayed = true;

        if (block.requireWatchVideo) {
            wait = video!.duration / 1000;
        } else {
            wait = waitUntilCanProceed;
        }

        restartButtonTransition();

        setTimeout(() => {
            canProceed = true;
        }, wait * 1000);
    }
</script>

{#if visible}
    <div
        class="flex size-full flex-1 flex-col items-center justify-around gap-4 py-6"
        bind:this={container}
        transition:fly={{ y: 48, delay: 350 }}
        on:introend={video == null ? startButtonTransition : undefined}
    >
        <div
            class="body grid w-full flex-1 grid-cols-1 items-center justify-center gap-4 text-center text-lg {hasMedia
                ? 'sm:grid-cols-2'
                : 'sm:grid-cols-1'}"
        >
            <div
                class="{order === 'media_text'
                    ? 'order-1'
                    : 'order-2'} {!hasMedia ? 'hidden' : ''}"
            >
                {#if videoSrc != null}
                    {@const poster = video?.preview_image}
                    <video
                        autoplay
                        controls
                        muted
                        preload="metadata"
                        width={videoSrc.width}
                        height={videoSrc.height}
                        poster={poster?.src}
                        use:setupVideo
                        on:play={handlePlay}
                    >
                        <source src={videoSrc.url} type="video/mp4" />
                    </video>
                {:else if imageSrc != null}
                    <ResponsiveImage
                        className="size-full"
                        image={{
                            src: imageSrc,
                            width: 400,
                            height: 200,
                        }}
                    />
                {/if}
            </div>
            <div
                class="{order === 'text_media'
                    ? 'order-1'
                    : 'order-2'} flex flex-col items-center gap-4"
            >
                <div>
                    {@html block.text}
                </div>
                <button
                    on:click={goToNextStep}
                    disabled={!canProceed}
                    class="
        relative flex-shrink-0 overflow-hidden rounded px-4 py-2 text-xl font-medium
        transition-all duration-200
      "
                    class:hover:scale-105={canProceed}
                    class:hover:shadow-xl={canProceed}
                >
                    Next
                    <div
                        bind:this={nextButtonOverlayContainer}
                        class="
            next-button-overlay
            absolute inset-0 ml-0 bg-white
            opacity-65 transition-[margin-left,shadow] ease-linear
        "
                        style="--wait-duration: {wait}s;"
                        on:transitionend={() => {
                            canProceed = true;
                        }}
                    ></div>
                </button>
            </div>
        </div>
    </div>
{/if}

<style lang="scss">
    button {
        background-color: var(--primary-color);
        color: var(--text-on-primary-color);
    }

    .next-button-overlay {
        transition-duration: var(--wait-duration);
    }

    .body {
        :global(img),
        :global(video) {
            width: 100%;
            max-height: 100%;
            border-radius: 8px;
        }
    }
</style>
